<template>
    <div class="container-fluid"
        v-if="details != undefined && details.fileName != undefined">
        <div class="w-100 text-start">
            <h2 class="mx-auto">{{ name }} - <span class="text-color-darkgray">{{ details.fileName }}</span></h2>
        </div>
        <div class="alert alert-danger show"
            role="alert"
            v-if="details.errorCode != null && details.errorMessage != null && details.errorCode.length > 0">
            <strong>File Error: </strong> {{ details.errorCode }} - {{ details.errorMessage }}
        </div>
        <div class="alert alert-danger show"
            role="alert"
            v-else-if="details.errorMessage">
            <strong>File Error: </strong> {{ details.errorMessage }}
        </div>
        <dl id="uploadDetailsInfo"
            class="inline-flex">
            <dt><span class="title-bold">Uploaded by:</span></dt>
            <dd>
                <span class="val">{{ details.createdBy }}</span>
            </dd>
            <dt>
                <span class="title-bold">Uploaded on:</span>
            </dt>
            <dd>
                <span class="val">{{ details.createdOn }}</span>
            </dd>
            <dt>
                <span class="title-bold">Processed on:</span>
            </dt>
            <dd>
                <span class="val">{{ details.processedOn }}</span>
            </dd>
            <dt>
                <span class="title-bold">Status:</span>
            </dt>
            <dd>
                <span class="val">{{ details.statusLabel }}</span>
                <div class="table-responsive mb-3"
                    v-if="details.status?.toLowerCase() != 'failed' && details.fileStatistics?.length > 0">

                    <b-table :items="details.fileStatistics"
                        :columns="statisticsColumns"
                        :caption="'Error Rate Statistics'"
                        :value="statisticsTableOptions">
                        <template v-slot:cell.rejectionRate="{ item, column }">
                            <p style="width:70px;text-align: end;"
                                class="mb-0 d-inline-block">{{ item.rejectionRate }}
                            </p>
                        </template>
                    </b-table>
                </div>
            </dd>
            <dt v-if="details.status?.toLowerCase() === 'failed' && validationMessagesTableOptions.totalItemCount > 0">
                <span class="title-bold">Errors:</span>
            </dt>
            <dd v-if="details.status?.toLowerCase() === 'failed' && (validationMessagesTableOptions.totalItemCount > 0 || validationMessagesFilterOptions.filters.length > 0)"
                style="overflow:initial">
                <div class="">
                    <b-filter-modal :isShowFilterEditor="isValidationMessagesFilterEditorVisible"
                        :filterOptions="validationMessagesFilterOptions"
                        :filterableFields="validationMessagesFilterableFields"
                        @filterChanged="applyValidationMessagesFilters"
                        @dismissModal="handleDismiss"></b-filter-modal>
                    <b-table-header :value="validationMessagesTableOptions"
                        header-slot-class="b-header-slot w-100"
                        @optionChange="handleValidationMessagesOptionChange"
                        style="max-width: 800px;"
                        :current-item-count="numberOfReturnedItems">
                        <template #default>
                            <button @click.prevent="showValidationMessagesFilterEditor()"
                                class="btn btn-primary">
                                <i class="bi bi-search pe-1"></i>
                                Filter
                            </button>
                            <slot name="header.toolbar"></slot>
                            <span class="filter-description"
                                v-if="validationMessagesFilterOptions.filters.length > 0">
                                {{ validationMessagesFiltersDescription() }}
                            </span>
                            <button v-if="validationMessagesFilterOptions.filters.length > 0"
                                class="btn-close"
                                v-tooltip="'Remove all currently applied filters.'"
                                @click="resetValidationMessagesFilters()">
                            </button>
                        </template>
                        <template #headerTopBar >
                            <div class="btn-group ms-auto"
                                style=""
                                role="group"
                                v-if="store.showWarningDetails">
                                <drop-down label="Error/Warning Details" :item-count="2" :is-primary-button="false">
                                    <template v-slot:item1="{}">
                                        <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}csv`">Download as .csv</a>
                                    </template>
                                    <template v-slot:item2="{}">
                                        <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}xlsx`">Download as .xlsx</a>
                                    </template>
                                </drop-down>
                            </div>
                        </template>
                    </b-table-header>
                    <b-table :items="failedFileValidationMessages"
                        :columns="validationMessagesColumns"
                        :caption="'Validation Messages'"
                        :value="validationMessagesTableOptions"
                        :table-style="'max-width: 800px;'"
                        @optionChange="handleValidationMessagesOptionChange"
                        >
                        <template #cell.errorCode="{ item }">
                            <button type="button"
                                class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                                v-on:click.prevent="handleErrorCodeClick(item)">{{ item["errorCode"] }}</button>
                        </template>
                    </b-table>
                    <b-table-header :value="validationMessagesTableOptions"
                        @optionChange="handleValidationMessagesOptionChange"
                        header-slot-class="b-header-slot w-100"
                        style="max-width: 800px;"
                        :current-item-count="numberOfReturnedItems">
                    </b-table-header>
                </div>
            </dd>

        </dl>
        <information-modal :title="selectedErrorCode"
            :show-modal="showErrorDescriptionModal" @dismiss="handleErrorDescriptionModalDismiss">
            <template #body>
                <div v-html="selectedErrorBody">
                </div>
            </template>
        </information-modal>
        <div v-show="showErrorsAndWarnings"
            class="mt-2 position-relative">
            <h4 id="errorTitle"
                class="title-bold position-relative" style="top:45px">Errors and Warnings:</h4>

            <b-filter-modal :isShowFilterEditor="isShowFilterEditor"
                :filterOptions="filterOptions"
                :filterableFields="errorsAndWarningsFilterableFields"
                @filterChanged="applyFilters"
                @dismissModal="handleDismiss"></b-filter-modal>

            <b-table-header :value="options"
                @optionChange="handleOptionChange($event)"
                header-slot-class="b-header-slot w-100"
                :current-item-count="numberOfReturnedItems">
                <template #default>
                    <button @click="showFilterEditor($event)"
                        class="btn btn-primary filter-button-width" v-if="errorsAndWarningsFilterableFields.length > 0">
                        <i class="bi bi-search pe-1"></i>
                        Filter
                    </button>
                    <slot name="header.toolbar"></slot>
                    <span class="filter-description"
                        v-if="hasAppliedFilters">
                        {{ filtersDescription }}
                    </span>
                    <button v-if="hasAppliedFilters"
                        class="btn-close"
                        v-tooltip="'Remove all currently applied filters.'"
                        @click="(event) => resetFilters()">
                    </button>
                </template>
                <template #headerTopBar>
                    <div class="btn-group ms-auto" role="group" v-if="store.showWarningDetails">
                        <drop-down label="Error/Warning Details" :item-count="2" :is-primary-button="false">
                            <template v-slot:item1="{}">
                                <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}csv`">Download as .csv</a>
                            </template>
                            <template v-slot:item2="{}">
                                <a class="dropdown-item" :href="`${store.errorDescriptionsDownloadUrl}xlsx`">Download as .xlsx</a>
                            </template>
                        </drop-down>
                    </div>
                </template>
                <template #pagingStart>
                    <div class="btn-group" role="group" v-if="store.showWarningDetails">
                        <drop-down label="Export Data" :item-count="2" :is-primary-button="true">
                            <template v-slot:item1="{ item }">
                                <a class="dropdown-item"
                                    href="javascript:void"
                                    @click.prevent="handleErrorExport('csv')">Download
                                    as .csv</a>
                            </template>
                            <template v-slot:item2="{ item }">
                                <a class="dropdown-item"
                                    href="javascript:void"
                                    @click.prevent="handleErrorExport('xlsx')">Download
                                    as .xlsx</a>
                            </template>
                        </drop-down>                            
                    </div>
                </template>
            </b-table-header>
            <b-table :items="errorsAndWarnings"
                :columns="columns"
                :caption="'List of errors and warnings'"
                :value="options"
                @optionChange="handleOptionChange($event)">
                <template v-slot:cell.agency="{ item, column }">
                    <span v-html="item.agency"></span>
                </template>
                <template #cell.incidentNumber="{ item, column }">
                    <a class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                        :href="generateLinkToEditor(item)"
                        v-if="enableLinkToEditor && shouldHaveLinkToIE(item)">{{ item["incidentNumber"] }}</a>
                    <span v-else>{{ item["incidentNumber"] }}</span>
                </template>
                <template #cell.errorCode="{ item }">
                    <button type="button"
                        class="btn btn-link kendo-stop-hiding-my-links ms-0 ps-0 text-start"
                        v-on:click.prevent="handleErrorCodeClick(item)">{{ item["errorCode"] }}</button>
                </template>
            </b-table>
            <b-table-header :value="options"
                @optionChange="handleOptionChange($event)"
                header-slot-class="b-header-slot w-100"
                :current-item-count="numberOfReturnedItems">
            </b-table-header>
        </div>
    </div>
    <div class="container-fluid"
        v-else>
        <div class="header w-100 text-center">
            <h3 class="mx-auto">Loading...</h3>
        </div>
    </div>
</template>
<script setup lang="ts">
import { AxiosResponse } from "axios";
import { computed, defineComponent, ref, watch } from "vue";
import {
    UploadedFileDetails,
    UploadedFileWarningsAndErrors,
    FileStatistics,
    FailedFileDetails,
} from "../types";

import BTable from "./BTable/BTable.vue";
import BTableHeader from "./BTable/BTableHeader.vue";
import { BTableOptions, BTableColumn } from "./BTable/BTable.types";
import { serializeFilters, serializeSortBy } from "./BTable/HelperFunctions";
import BFilterEditor from "./BFilterEditor/BFilterEditor.vue";
import BFilterModal from "./BFilterEditor/BFilterModal.vue";
import InformationModal from "./Misc/InformationModal.vue";

import {
    BFilterOptions,
    BFilter,
    BFilterableField,
    BFieldSelection,
    describeFilters,
} from "./BFilterEditor/BFilterEditor.types";
import { useCommunityPolicingStore } from "@/stores/communityPolicingStore";
import { useContactsStore } from "@/stores/contactsStore";
import { useNibrsStore } from "@/stores/nibrsStore";
import { useUofStore } from "@/stores/uofStore";
import { useSummaryStore } from "@/stores/summaryStore";
import { UploadType } from "@/types";
import { useNarrativeStore } from "@/stores/narrativeStore";
import { usePoliceEmployeeStore } from "@/stores/policeEmployeeStore";
import { usePopulationStore } from "@/stores/populationStore";
import { getHtmlContent } from '@/utils';
import {validationMessagesFilterableFields} from '@/tableDefaults';
import DropDown from "@/components/Misc/DropDown.vue";

const props = defineProps<{
    name: string;
    url: string;
    id: number;
    onlyHasOneOri: boolean;
    dataset: string;
    enableLinkToEditor: boolean;
    editorLink: string;
    exportUrl: string;
    descriptionUrl: string;
    noTotal:boolean;
}>();
var store = {} as any;
    var uploadType: UploadType = UploadType[props.dataset ?? ""];

    switch (uploadType) {
        case UploadType.contacts:
            store = useContactsStore();
            break;
        case UploadType.communityPolicing:
            store = useCommunityPolicingStore();
            break;
        case UploadType.nibrs:
            store = useNibrsStore();
            break;
        case UploadType.summary:
            store = useSummaryStore();
            break;
        case UploadType.useOfForce:
            store = useUofStore();
            break;
        case UploadType.narrative:
            store = useNarrativeStore();
            break;
        case UploadType.policeEmployee:
            store = usePoliceEmployeeStore();
            break;
        case UploadType.population:
            store = usePopulationStore();
            break;
    }
var details = ref({} as UploadedFileDetails);
var failedFileValidationMessages = ref([] as FailedFileDetails[]);
var isShowFilterEditor = ref(false);
var isValidationMessagesFilterEditorVisible = ref(false);
var hasAppliedFilters = ref(false);
var options = ref({
    totalItemCount: 0,
    itemsPerPage: 25,
    page: 1,
    sortBy: [],
    filterBy: [],            
    pagingMode: (props.noTotal?"noTotal":"withTotal")
} as BTableOptions);
var filterOptions = ref({
    filters: [],
} as BFilterOptions);
var showErrorDescriptionModal = ref(false);
var selectedErrorDescription = ref("");
var selectedErrorCode = ref("");
var selectedErrorType = ref("");
var selectedErrorBody = ref("");
var validationMessagesTableOptions = ref({
    totalItemCount: 0,
    itemsPerPage: 25,
    page: 1,
    sortBy: [],
    filterBy: [],            
    pagingMode: "noTotal"
} as BTableOptions);
var validationMessagesFilterOptions = ref({
    filters: [],
} as BFilterOptions);
var numberOfReturnedItems = ref(0);
var errorsAndWarningsFilterableFields = computed(():BFilterableField[] => {
    return store.getFilterableFieldsForErrorsAndWarnings(props.onlyHasOneOri, false, true) as BFilterableField[];
});

var columns = computed((): BTableColumn[] => {
    return store.getErrorsAndWarningsColumns(props.onlyHasOneOri, false, true);
});

const statisticsColumns = computed(() => {
    if (props.dataset == "population") {
        return [
            {
                key: "type",
                text: "",
                sortable: false,
                value: (item) => item.type,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
            {
                key: "submitted",
                text: "Added",
                sortable: false,
                value: (item) => item.submitted,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
            {
                key: "rejected",
                text: "Updated",
                sortable: false,
                value: (item) => item.rejected,
                columnClass: ["text-center"],
                class: (item) => ["text-center"],
            },
        ] as BTableColumn[];
    }
    return [
        {
            key: "type",
            text: "",
            sortable: false,
            value: (item) => item.type,
            columnClass: ["text-center"],
            class: (item) => ["text-center"],
        },
        {
            key: "submitted",
            text: "Submitted",
            sortable: false,
            value: (item) => item.submitted,
            columnClass: ["text-center"],
            class: (item) => ["text-center"],
        },
        {
            key: "rejected",
            text: "Rejected",
            sortable: false,
            value: (item) => item.rejected,
            columnClass: ["text-center"],
            class: (item) => ["text-center"],
        },
        {
            key: "rejectionRate",
            text: "Rejection Rate",
            sortable: false,
            value: (item) => item.rejectionRate,
            columnClass: ["text-center"],
            class: (item) => ["align-center"],
        },
    ] as BTableColumn[];
});

const statisticsTableOptions = computed(() => {
    return {
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        filterBy: [],
    } as BTableOptions;
});

const validationMessagesColumns = computed(() => {
    return store.getValidationMessagesColumns;
});

const filtersDescription = computed(() => {
    if (filterOptions == null) {
        return "";
    }
    return describeFilters(
        filterOptions.value.filters,
        errorsAndWarningsFilterableFields.value
    );
});


const showErrorsAndWarnings = computed(() => {
    return (
        details.value.status?.toLowerCase() !== "failed" &&
        details.value.status?.toLowerCase() !== "completed"
    );
});
 
var errorsAndWarnings = ref<UploadedFileWarningsAndErrors[]>(
    [] as UploadedFileWarningsAndErrors[]
);
fetchDetails();

function fetchDetails() {
    store.getRecordDetails(props.id)
        .then((response: AxiosResponse) => {
            details.value = response.data;
            let status = response.data.status.toLowerCase();
            if (status == "failed") {
                fetchValidationMessages();
            } else if (status != "completed" && status != "pending") {
                fetchErrorsAndWarnings();
            }
        });
}
function fetchValidationMessages() {
    let filters = [] as BFilter[];
    if (validationMessagesFilterOptions.value.filters != undefined) {
        filters = validationMessagesFilterOptions.value.filters;
    }

    store.getValidationMessagesForFile(
            props.id,
            validationMessagesTableOptions.value.page,
            validationMessagesTableOptions.value.itemsPerPage,
            filters,
            validationMessagesFilterableFields,
            validationMessagesTableOptions.value.sortBy
        )
        .then((response: AxiosResponse) => {
            failedFileValidationMessages.value = response.data.data;
            validationMessagesTableOptions.value.totalItemCount = response.data.total;
            numberOfReturnedItems.value = response.data.data.length;    
        });
}

function fetchErrorsAndWarnings() {
    let filters = [] as BFilter[];
    if (hasAppliedFilters) {
        filters = filterOptions.value.filters;
    }
    store.getErrorsAndWarningsForFile(props.id, filters, options.value, errorsAndWarningsFilterableFields.value, options.value.sortBy)
    .then((response:AxiosResponse) => {
        if(options.value.pagingMode == "noTotal" && response?.data?.data?.length > 0) {
                //When were hiding the total text,first button, and last button then we return itemsPerPage + 1 in our get data call. 
                //If the returned amount of rows is greater than itemsPerPage then enable the next button otherwise disable the next button to prevent an error
                errorsAndWarnings.value = response.data.data.slice(0,options.value.itemsPerPage);                    
            }
            else{
                errorsAndWarnings.value = response.data.data;
            }
            numberOfReturnedItems.value = response.data.data.length;            
            options.value.totalItemCount = response.data.total;
        })
        .catch((error:any) => {
            console.error(error?.response?.statusText);
        });

    return store.getErrorsAndWarningsForFile(
        props.id,
        filters,
        options.value,
        errorsAndWarningsFilterableFields.value,
        options.value.sortBy
    );
}

function resetFilters() {
    if (filterOptions.value != null) {
        filterOptions.value.filters = [];
        options.value.page = 1;
        fetchErrorsAndWarnings();
    }
    isShowFilterEditor.value = false;
    hasAppliedFilters.value = false;
}

function applyFilters(newFilterOptions: BFilterOptions) {
    filterOptions.value = newFilterOptions;
    isShowFilterEditor.value = false;
    hasAppliedFilters.value = newFilterOptions.filters.length > 0;

    options.value.page = 1;

    fetchErrorsAndWarnings();
}

function showFilterEditor(event: any) {
    event.preventDefault();

    isShowFilterEditor.value = true;
}

function handleDismiss() {
    isShowFilterEditor.value = false;
    isValidationMessagesFilterEditorVisible.value = false;
}

function handleOptionChange(newOptions: BTableOptions) {
    if (newOptions) {
        options.value = newOptions;
    }
    fetchErrorsAndWarnings();
}
//Start of Validation Messages methods
function resetValidationMessagesFilters() {
    if (validationMessagesFilterOptions.value != null) {
        validationMessagesFilterOptions.value.filters = [];

        fetchValidationMessages();
    }
    isValidationMessagesFilterEditorVisible.value = false;
}

function applyValidationMessagesFilters(filterOptions: BFilterOptions) {
    validationMessagesFilterOptions.value = filterOptions;
    handleDismiss();

    if (validationMessagesFilterOptions.value.filters.length > 0) {
        validationMessagesTableOptions.value.page = 1;
    }

    fetchValidationMessages();
}

function showValidationMessagesFilterEditor() {
    isValidationMessagesFilterEditorVisible.value = true;
}

function handleValidationMessagesOptionChange(newOptions: BTableOptions) {
    if (newOptions) {
        validationMessagesTableOptions.value = newOptions;
    }
}

//End of errors grid methods
function handleErrorDescriptionModalDismiss() {
    showErrorDescriptionModal.value = false;
}
function handleErrorCodeClick(item: any) {
    let errorCode = item["errorCode"];
    selectedErrorCode.value = "";
    selectedErrorDescription.value = "";
    selectedErrorBody.value = "No information available";

    if (
        errorCode != undefined &&
        store.errorDescriptions &&
        store.errorDescriptions.value
    ) {
        selectedErrorCode.value = errorCode;
        store.errorDescriptions.value.forEach((f) => {
            if (f.code == errorCode) {
                selectedErrorType.value = item["errorType"];
                selectedErrorDescription.value = f.help;

                var descriptionText = "";

                getHtmlContent(`help.aspx?e=${selectedErrorCode.value}`)
                .then(data => descriptionText = data)
                .catch(err => descriptionText = '')
                .finally(() => {
                    if (descriptionText != '') {
                        selectedErrorBody.value = descriptionText;
                    }
                    else if (selectedErrorDescription.value != '') {
                        selectedErrorBody.value = selectedErrorDescription.value ;
                    }
                    else {
                        selectedErrorBody.value = 'No information available';
                    }
                    showErrorDescriptionModal.value = true;
                    return;
                });
            }
        });
        showErrorDescriptionModal.value = true;
    }
}

function validationMessagesFiltersDescription(): string {
    if (validationMessagesFilterOptions.value == null) {
        return "";
    }
    return describeFilters(
        validationMessagesFilterOptions.value.filters,
        validationMessagesFilterableFields
    );
}

function generateLinkToEditor(item: UploadedFileWarningsAndErrors) {
    var url = props.editorLink;
    url = url.replace(
        "{{incidentnumber}}",
        encodeURIComponent(item["incidentNumber"])
    );
    url = url.replace("{{ori}}", encodeURIComponent(item.ori));

    return url;
}

function shouldHaveLinkToIE(item: UploadedFileWarningsAndErrors) {
    if (item["isZeroReport"]) {
        return false;
    }
    return true;
}

function handleErrorExport(fileType: string) {
    let filters = [] as any[];

    if (hasAppliedFilters) {
        filters = filterOptions.value.filters;
    }

    var encodedFilter = encodeURIComponent(
        serializeFilters(filters, errorsAndWarningsFilterableFields.value)
    );
    var encodedSort = encodeURIComponent(serializeSortBy(options.value.sortBy));

    window.open(
        `${props.exportUrl}${fileType}?filter=${encodedFilter}&sort=${encodedSort}`,
        "_blank"
    );
}

watch(validationMessagesTableOptions, (validationMessagesTableOptions) => {
    if (validationMessagesTableOptions) {
        fetchValidationMessages();
    }
});
</script>